import React, { useState } from 'react';
import { Lock, Eye, EyeOff, AlertCircle, Shield } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import FonteyneLogo from './FonteyneLogo';
import api from '../services/api';

const Login = () => {
  const { login } = useAuth();
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await api.post('/api/v1/auth/login', { password });
      
      if (response.data.admin_token) {
        // Utiliser le contexte d'authentification
        login(response.data.admin_token);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-fonteyne-warm-gray via-primary-100 to-accent-100">
      <div className="bg-fonteyne-white rounded-2xl shadow-2xl p-10 w-full max-w-lg border border-primary-200">
        {/* Header avec logo Fonteyne */}
        <div className="text-center mb-10">
          <div className="mb-6">
            <FonteyneLogo className="h-12 w-auto mx-auto mb-4" />
          </div>
          <h1 className="text-3xl font-display font-bold text-fonteyne-black mb-2">
            Gestion de Flotte
          </h1>
          <p className="text-fonteyne-medium-gray font-medium tracking-wide uppercase text-sm">
            Accès Administrateur Sécurisé
          </p>
          <div className="w-20 h-0.5 bg-accent-300 mx-auto mt-4"></div>
        </div>

        {error && (
          <div className="bg-warning-50 border-2 border-warning-300 rounded-xl p-4 mb-8">
            <div className="flex items-center space-x-3">
              <AlertCircle className="h-5 w-5 text-warning-600" />
              <p className="text-sm font-medium text-warning-800">{error}</p>
            </div>
          </div>
        )}

        <form onSubmit={handleLogin} className="space-y-8">
          <div>
            <label htmlFor="password" className="block text-sm font-semibold text-fonteyne-charcoal mb-3 tracking-wide uppercase">
              Mot de passe
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-6 py-4 border-2 border-primary-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-accent-300 focus:border-accent-300 pr-14 bg-fonteyne-white font-medium placeholder-fonteyne-medium-gray transition-all duration-300"
                placeholder="Saisissez votre mot de passe"
                required
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-fonteyne-medium-gray hover:text-fonteyne-charcoal transition-colors duration-200"
                disabled={loading}
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading || !password}
            className={`w-full py-4 px-6 rounded-xl font-semibold text-lg transition-all duration-300 transform ${
              loading || !password
                ? 'bg-primary-200 text-primary-400 cursor-not-allowed'
                : 'bg-fonteyne-charcoal hover:bg-fonteyne-black text-fonteyne-white shadow-xl hover:shadow-2xl hover:scale-105 active:scale-95'
            }`}
          >
            {loading ? (
              <div className="flex items-center justify-center space-x-3">
                <div className="w-5 h-5 border-2 border-fonteyne-white border-t-transparent rounded-full animate-spin"></div>
                <span>Connexion en cours...</span>
              </div>
            ) : (
              <div className="flex items-center justify-center space-x-3">
                <Lock className="h-5 w-5" />
                <span>Accéder à la Plateforme</span>
              </div>
            )}
          </button>
        </form>

        <div className="mt-10 text-center">
          <div className="flex items-center justify-center space-x-2 text-xs text-fonteyne-medium-gray">
            <Shield className="h-4 w-4" />
            <span className="font-medium tracking-wider uppercase">
              Plateforme Sécurisée Fonteyne The Kitchen
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
